import React, { useEffect, useState } from 'react'

import styled, { css, keyframes } from 'styled-components'

import { COLORS } from '@/constants'

const duration = '0.3s'
const switchWidth = '44px'
const switchHeight = '22px'
const colorChecked = COLORS.primary500
const colorUnchecked = '#ccc'
const handleBoxShadow = '0 2px 5px rgba(0, 0, 0, 0.26)'

// Keyframes for animations
const rcSwitchOn = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.25); }
  100% { transform: scale(1.1); }
`

const rcSwitchOff = keyframes`
  0% { transform: scale(1.1); }
  100% { transform: scale(1); }
`

// Styled component with conditional animation
const RcSwitch = styled.div`
  position: relative;
  display: inline-block;
  width: ${switchWidth};
  min-width: ${switchWidth};
  height: ${switchHeight};
  border-radius: 20px;
  border: 1px solid
    ${props => (props.isChecked ? colorChecked : colorUnchecked)};
  background-color: ${props =>
    props.isChecked ? colorChecked : colorUnchecked};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all ${duration} cubic-bezier(0.35, 0, 0.25, 1);
  overflow: hidden;

  &:after {
    position: absolute;
    width: 18px;
    height: 18px;
    top: 1px;
    left: ${props => (props.isChecked ? '22px' : '2px')};
    border-radius: 50%;
    background-color: #fff;
    content: ' ';
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    box-shadow: ${handleBoxShadow};
    transition: left ${duration} cubic-bezier(0.35, 0, 0.25, 1);

    /* Apply animation only if animate is true */
    ${props =>
    props.animate &&
    css`
        animation: ${props.isChecked ? rcSwitchOn : rcSwitchOff} ${duration}
          forwards;
      `};
  }

  &:hover:after {
    transform: ${props => (props.disabled ? 'scale(1)' : 'scale(1.1)')};
  }
`

export const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

export const SwitchLabel = styled.span<{ side: 'left' | 'right' }>`
  font-size: 14px;
  color: #333;
  margin: ${props => (props.side === 'left' ? '0 8px 0 0' : '0 0 0 8px')};
  user-select: none;
  cursor: pointer;
  font-weight: bold;
`

const SwitchV2= ({
     checked,
     disabled = false,
     options,
     onChange,
     label,
 }) => {
    const [isChecked, setIsChecked] = useState(checked)
    const [animate, setAnimate] = useState(false)

    // Update animation and checked state only when the checked prop changes
    useEffect(() => {
        if (isChecked !== checked) {
            setAnimate(true)
            setIsChecked(checked)
        }
    }, [checked])

    // Reset animation flag after animation completes
    useEffect(() => {
        if (animate) {
            const timeout = setTimeout(
                () => setAnimate(false),
                parseFloat(duration) * 1000,
            )
            return () => clearTimeout(timeout)
        }
    }, [animate])

    // Determine labels from options
    const leftLabel = options?.length ? options[0]?.label : ''
    const rightLabel = options?.length ? options[1]?.label : ''

    return (
        <SwitchContainer>
            {options && leftLabel && (
                <SwitchLabel
                    onClick={() => !disabled && onChange(!isChecked)}
                    side="left"
                >
                    {leftLabel}
                </SwitchLabel>
            )}

            <RcSwitch
                isChecked={isChecked}
                disabled={disabled}
                animate={animate}
                onClick={() => !disabled && onChange(!isChecked)}
            />

            {options && rightLabel && (
                <SwitchLabel
                    onClick={() => !disabled && onChange(!isChecked)}
                    side="right"
                >
                    {rightLabel}
                </SwitchLabel>
            )}

            {label && (
                <SwitchLabel
                    onClick={() => !disabled && onChange(!isChecked)}
                    side="right"
                >
                    {label}
                </SwitchLabel>
            )}
        </SwitchContainer>
    )
}

export { SwitchV2 }
